import type { BeneficiaryAgencyScope } from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/BeneficiaryAgencyScope';
import type { ComposedOrganization } from '@UI/Providers/CurrentOrganizationProvider/helpers/composeAssignedOrganizations';

export type AgencyScopeOption = {
	readonly key: string;
	readonly label: string;
	readonly depth: 0 | 1;
	readonly scope: BeneficiaryAgencyScope;
	readonly facilityIds: readonly number[];
};

/**
 * Builds the "Agence" filter options: "Toutes mes agences" first, then one group per organization that
 * has at least one facility. With a single organization the group row is omitted (it would duplicate
 * "Toutes mes agences") and its facilities are not indented.
 */
const buildAgencyScopeOptions = (
	organizations: readonly ComposedOrganization[],
): readonly AgencyScopeOption[] => {
	const organizationsWithFacilities = organizations.filter(
		(organization) => organization.facilities.length > 0,
	);
	const hasSingleOrganization = organizationsWithFacilities.length === 1;
	const options: AgencyScopeOption[] = [
		{ key: 'all', label: 'Toutes mes agences', depth: 0, scope: { type: 'all' }, facilityIds: [] },
	];

	for (const organization of organizationsWithFacilities) {
		if (!hasSingleOrganization) {
			options.push({
				key: `organization:${organization.id}`,
				label: `Mes agences de ${organization.name}`,
				depth: 0,
				scope: { type: 'organization', organizationId: organization.id },
				facilityIds: organization.facilities.map((facility) => facility.id),
			});
		}

		const sortedFacilities = [...organization.facilities].sort((left, right) =>
			left.name.localeCompare(right.name),
		);

		for (const facility of sortedFacilities) {
			options.push({
				key: `facility:${facility.id}`,
				label: facility.name,
				depth: hasSingleOrganization ? 0 : 1,
				scope: { type: 'facility', facilityId: facility.id },
				facilityIds: [facility.id],
			});
		}
	}

	return options;
};

export default buildAgencyScopeOptions;
